import { useCallback, useState } from "react";
import { SpinButton } from "@fluentui/react/lib/SpinButton";

import { isNullOrWhiteSpace } from "Utils/String";
import { WithConfiguration } from "Components/Generic/Configuration";
import { WithTranslator } from "Components/Generic/Translator";
import { clampValue } from "Utils/Number";
import { IMonthsProps } from "./IMonthsProps";

const boundary = {
  min: 1,
  max: 24,
  step: 6
};

export const Months = WithTranslator(
  WithConfiguration((props: IMonthsProps): JSX.Element => {
    const {
      className, onChange, config, translator
    } = props;
    const lastXMonths = config?.getLoadLatestXMonths() <= 6 ? config?.getLoadLatestXMonths() : 6;
    const [months, setMonths] = useState(lastXMonths);

    const onMonthsChange = useCallback(
      (_, newMonths?: string) => {
        const value = clampValue(isNullOrWhiteSpace(newMonths) ? 6 : parseInt(newMonths, 10), boundary);
        setMonths(() => value);
        onChange(value);
      },
      [onChange, setMonths]
    );

    const onIncrement = useCallback((): void => {
      const rest = months % boundary.step;
      if (rest === 0) {
        onMonthsChange(null, (months + boundary.step).toString());
        return;
      }

      onMonthsChange(null, (months + boundary.step - rest).toString());
    }, [months, onMonthsChange]);

    const onDecrement = useCallback((): void => {
      onMonthsChange(null, (months - boundary.step).toString());
    }, [months, onMonthsChange]);

    return (
      <SpinButton
        onChange={onMonthsChange}
        onIncrement={onIncrement}
        onDecrement={onDecrement}
        value={`${months} ${translator.get(`settings.sync.months.short`, `mo.`)}`}
        className={className}
      />
    );
  })
);
